"use client";

import { useEffect, useRef, useState } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import ColorPicker from "@/components/ui/color-picker";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { buttonVariants } from "@/components/ui/button";
import { useLocale } from "@/hooks/useLocale";
import {
    startUndoGroup,
    useLomdaV3Editor,
    type UndoGroup,
} from "../../store/useLomdaV3Editor";

/**
 * Shared chrome for second-level topbar buttons.
 *
 * The topbar sits OUTSIDE the lomda preview, so it is platform UI: this is the
 * shadcn outline button as-is, which also makes its hover identical to the
 * rich-text toolbar's ghost buttons (both `accent`).
 */
export const chromeBtn = cn(
    buttonVariants({ variant: "outline", size: "sm" }),
    "h-9 shrink-0 gap-2 rounded-lg"
);

/** Shared slider styling. The shadcn track (`bg-secondary`) is too faint
 *  on the card-coloured topbar, so it is forced to Border — the PLATFORM one,
 *  since the topbar is not part of the lomda. */
const sliderClass = cn(
    "w-[124px] data-[disabled]:opacity-50",
    "[&>span:first-child]:h-1.5 [&>span:first-child]:bg-border",
    "[&_[role=slider]]:size-3 [&_[role=slider]]:border-2 [&_[role=slider]]:border-primary [&_[role=slider]]:bg-card"
);

/** Open-state for a chromeBtn that triggers a popover. */
export const chromeBtnOpen = "data-[state=open]:bg-accent";

/** Shared appearance/manage popover shell (Figma: 20px radius). Callers add
 *  their own width. Platform tokens — the topbar is not part of the lomda. */
export const popoverShell =
    "rounded-[20px] border border-border bg-card p-4 shadow-md";

/** Title of any topbar dropdown/popover — platform chrome, not lomda type. */
export function PopoverTitle({
    children,
    id,
}: {
    children: React.ReactNode;
    id?: string;
}) {
    return (
        <span id={id} className="text-lg font-medium text-foreground">
            {children}
        </span>
    );
}

/** The number box with its unit. Clamps a typed value to `min`–`max`. */
export function NumberField({
    value,
    onChange,
    label,
    min = 0,
    max = 100,
    unit = "%",
    disabled,
}: {
    value: number;
    onChange: (value: number) => void;
    label?: string;
    min?: number;
    max?: number;
    unit?: string;
    disabled?: boolean;
}) {
    const [input, setInput] = useState(String(value));
    useEffect(() => setInput(String(value)), [value]);
    const commit = () => {
        const n = parseInt(input.replace(/\D/g, ""), 10);
        const v = Number.isNaN(n) ? value : Math.min(max, Math.max(min, n));
        if (v !== value) onChange(v);
        setInput(String(v));
    };
    return (
        // Wider than Figma's 56px so "100%" fits at the Paragraph size.
        // LTR so RTL reads "100%", not "% 100".
        <div
            dir="ltr"
            className="text-sm flex h-9 w-[72px] items-center justify-center rounded-lg border border-border bg-card px-2 text-foreground has-[:disabled]:opacity-50"
        >
            <input
                value={input}
                aria-label={label}
                disabled={disabled}
                inputMode="numeric"
                onChange={(e) =>
                    setInput(
                        e.target.value
                            .replace(/\D/g, "")
                            .slice(0, String(max).length)
                    )
                }
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                }}
                className="w-8 bg-transparent text-center outline-none"
            />
            <span className="text-muted-foreground">{unit}</span>
        </div>
    );
}

export function NumberSliderField({
    value,
    onChange,
    label,
    min = 0,
    max = 100,
    step = 5,
    unit = "%",
    disabled,
}: {
    value: number;
    onChange: (value: number) => void;
    label?: string;
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    disabled?: boolean;
}) {
    const { dir } = useLocale();
    const drag = useRef<UndoGroup | null>(null);
    // A press that changes nothing gets no commit, so release ends it too.
    const endDrag = () => {
        drag.current?.end();
        drag.current = null;
    };
    return (
        <>
            <NumberField
                value={value}
                onChange={onChange}
                label={label}
                min={min}
                max={max}
                unit={unit}
                disabled={disabled}
            />
            <Slider
                dir={dir}
                value={[value]}
                min={min}
                max={max}
                step={step}
                disabled={disabled}
                aria-label={label}
                onPointerDown={() => {
                    if (!disabled) drag.current = startUndoGroup();
                }}
                onValueChange={([v]) => onChange(v)}
                onValueCommit={endDrag}
                onPointerUp={endDrag}
                onLostPointerCapture={endDrag}
                className={sliderClass}
            />
        </>
    );
}

/** Block width control (input + slider). Present on every block's topbar. */
export function WidthControl({ blockId }: { blockId: string }) {
    const setBlockWidth = useLomdaV3Editor((s) => s.setBlockWidth);
    const blockWidth = useLomdaV3Editor(
        (s) => s.content.blockWidths?.[blockId] ?? 100
    );
    const label = t("lomda-v3-editor.text.width");
    return (
        <div className="flex h-9 shrink-0 items-center gap-2">
            <span className="text-sm text-foreground">{label}</span>
            <NumberSliderField
                label={label}
                value={blockWidth}
                onChange={(v) => setBlockWidth(blockId, v)}
            />
        </div>
    );
}

/** One-of-N buttons (steps display, divider thickness/spacing). Picking the
 *  current option again changes nothing. */
export function SegmentedToggle<T extends string>({
    label,
    value,
    onChange,
    options,
    fill = false,
}: {
    label: string;
    value: T;
    onChange: (value: T) => void;
    options: { value: T; content: React.ReactNode; name?: string }[];
    /** Stretch to the full width, options sharing it equally. */
    fill?: boolean;
}) {
    const { dir } = useLocale();
    return (
        <ToggleGroup
            type="single"
            dir={dir}
            value={value}
            onValueChange={(next) => {
                if (next && next !== value) onChange(next as T);
            }}
            aria-label={label}
            className={cn(
                "h-9 shrink-0 gap-0.5 rounded-lg border p-0.5",
                fill && "w-full"
            )}
        >
            {options.map((o) => (
                <ToggleGroupItem
                    key={o.value}
                    value={o.value}
                    aria-label={o.name}
                    title={o.name}
                    className={cn(
                        "h-full min-w-0 whitespace-nowrap rounded-md px-3",
                        fill && "flex-1 basis-0"
                    )}
                >
                    {o.content}
                </ToggleGroupItem>
            ))}
        </ToggleGroup>
    );
}

/** Vertical divider separating topbar setting groups. */
export const ToolbarDivider = () => (
    <span className="h-6 w-px shrink-0 bg-border" />
);

/** Full-width divider between appearance-popover groups. */
export const AppearanceDivider = () => (
    <span className="h-px w-full bg-border" />
);

/** "Reset to default" action at the foot of every appearance popover — reverts
 *  the block's colors/borders back to the theme's default look. One definition;
 *  each popover passes its own reset handler. */
export function AppearanceReset({
    onReset,
    label = t("lomda-v3-editor.appearance.reset"),
}: {
    onReset: () => void;
    label?: string;
}) {
    return (
        <>
            <AppearanceDivider />
            <button
                type="button"
                onClick={onReset}
                className="text-sm flex items-center gap-1.5 self-start text-muted-foreground transition hover:text-foreground"
            >
                <RotateCcw className="size-4" />
                {label}
            </button>
        </>
    );
}

export const hex = (c: string) => c.replace(/^#/, "").toUpperCase();

/** A label + control row inside the appearance popover. */
export function AppearanceRow({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="flex h-9 items-center justify-between gap-4">
            <span className="min-w-0 truncate text-sm text-foreground">
                {label}
            </span>
            {children}
        </div>
    );
}

/** A −/value/+ stepper used inside appearance popovers (columns, rows, …). */
export function AppearanceStepper({
    value,
    onDec,
    onInc,
    canDec,
    canInc,
}: {
    value: number;
    onDec: () => void;
    onInc: () => void;
    canDec: boolean;
    canInc: boolean;
}) {
    return (
        <div className="flex h-9 items-center gap-1 rounded-lg border border-border">
            <button
                type="button"
                aria-label="-"
                disabled={!canDec}
                onClick={onDec}
                className="flex size-8 items-center justify-center text-foreground transition hover:text-primary disabled:opacity-30"
            >
                <Minus className="size-4" />
            </button>
            <span className="text-sm w-6 text-center text-foreground">
                {value}
            </span>
            <button
                type="button"
                aria-label="+"
                disabled={!canInc}
                onClick={onInc}
                className="flex size-8 items-center justify-center text-foreground transition hover:text-primary disabled:opacity-30"
            >
                <Plus className="size-4" />
            </button>
        </div>
    );
}

// One drag in the picker is one undo step, like a slider. The picker follows
// the mouse outside itself, so the release is caught on the window.
function startColorDrag() {
    const group = startUndoGroup();
    const end = () => {
        group.end();
        window.removeEventListener("pointerup", end);
        window.removeEventListener("pointercancel", end);
    };
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
}

/** A Muted chip showing a swatch + hex, opening a color picker on click. */
export function ColorChipField({
    value,
    onChange,
    label,
}: {
    value: string;
    onChange: (v: string) => void;
    label?: string;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label={label}
                    className="flex h-9 w-[110px] shrink-0 items-center gap-1.5 rounded-lg bg-muted px-2.5"
                >
                    <span
                        className="size-5 shrink-0 rounded border border-border"
                        style={{ backgroundColor: value }}
                    />
                    <span className="text-sm text-foreground">
                        {hex(value)}
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-3">
                <div onPointerDown={startColorDrag}>
                    <ColorPicker
                        initialColor={value}
                        onColorChange={onChange}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
}
